import { makeAutoObservable, runInAction } from 'mobx';
import capitalize from 'lodash.capitalize';
import { createForm } from 'forms/resetPasswordEmailForm';
import { Match } from 'navigo';
import { LoadStatus, HttpException, ExtendedHttpException } from 'utils/types';
import { serverUri } from 'utils/config';
import { localUserNotFound, Source, userNotFoundMessage } from 'dtos/users/common';

export class ResetPasswordEmailStore {
  form = createForm();
  status: LoadStatus = 'none';
  message?: string;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  activate(match: Match) {
    this.status = 'none';
    this.message = undefined;
    this.form.reset();
  }

  async sendEmail() {
    this.status = 'loading';
    this.message = undefined;
    const { isValid } = await this.form.validate({ showErrors: true });
    if (!isValid) {
      return;
    }
    const { email } = this.form.values();
    try {
      const response = await fetch(`${serverUri}/user/password/reset/${encodeURIComponent(email)}`, {
        method: 'GET',
      });
      if (response.ok) {
        runInAction(() => {
          this.status = 'success';
          this.message = undefined;
        });
      } else {
        const exception: HttpException = await response.json();
        if (exception.message === userNotFoundMessage) {
          runInAction(() => {
            this.status = 'error';
            this.message = `We couldn't find an account with that email address. Please double-check for any typos or try another email you might have used to sign up.`;
          });
        } else if (exception.message === localUserNotFound) {
          const localUserNotFoundException = exception as ExtendedHttpException;
          runInAction(() => {
            this.status = 'error';
            this.message = `Your account is linked to ${capitalize(
              localUserNotFoundException?.user?.source || Source.GOOGLE
            )} SSO, so we can't reset your password directly. Please log in via ${capitalize(
              localUserNotFoundException?.user?.source || Source.GOOGLE
            )} to access your account.`;
          });
        } else {
          runInAction(() => {
            this.status = 'error';
            this.message = undefined;
          });
        }
      }
    } catch (err) {
      runInAction(() => {
        this.status = 'error';
        this.message = 'Unable to send password-reset email. Please try again.';
      });
    }
  }
}
